import {
  Component,
  ViewChild,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ElementRef,
  HostListener,
  Output,
  EventEmitter,
  AfterContentInit,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { PhxImagePanZoomSizeObject, PhxImageSpatialDimensions } from './phx-image-pan-zoom.types';

@Component({
  selector: 'phx-image-pan-zoom',
  styleUrls: ['./phx-image-pan-zoom.component.scss'],
  templateUrl: './phx-image-pan-zoom.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-image-pan-zoom',
    '[class.phx-image-pan-zoom-blur]': 'phxBlurImage',
  },
})
export class PhxImagePanZoomComponent implements OnChanges, AfterContentInit {
  @ViewChild('viewImageElement', { static: true }) public viewImageElement: ElementRef;
  @Input() public phxImageSrcUrl: string;
  @Input() public phxCrossOrigin: string | null = 'Anonymous';
  @Input() public phxParentControlsImageVisibility: boolean = false;
  @Input() public phxFitToContainer: boolean = false;
  @Input() public phxInitialScale: number = 1.0; // Scale factor for initial image size (0.5 = 50%, 1.0 = fit to container)
  @Input() public phxBlurImage: boolean = false;
  @Output() public phxOnImageLoadComplete: EventEmitter<undefined> = new EventEmitter();

  public isImageVisible: boolean = false;
  public safeImageUrl: SafeUrl;
  private panEnabled: boolean = false;
  private imageElm: HTMLImageElement;
  private container: HTMLElement;
  private currentScale: number = 1;
  private matrixValues: number[] = [1, 0, 0, 1, 0, 0]; // [scaleX, skewX, skewY, scaleY, translateX, translateY]
  private positionsArray: number[][] = [];
  private animationTimeout: any;
  private imageDimensions: ClientRect;
  private imageContainerDimensions: ClientRect;
  private readonly ZOOM_VALUE: number = 0.6;
  private readonly minScale: number = 1;
  private readonly maxZoomValue: number = 200;
  private readonly zoomAnimationTime: number = 500;
  private readonly PAN_EPSILON: number = 0.5;
  public readonly inDemoMode: boolean;

  constructor(private cdr: ChangeDetectorRef, private _sanitizer: DomSanitizer) { }

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxImageSrcUrl } = changes;

    if (phxImageSrcUrl) {
      this.updateSafeImageUrl();
    }
  }

  public ngAfterContentInit() {
    this.imageElm = this.viewImageElement.nativeElement;
    this.container = this.imageElm.parentElement;
  }

  @HostListener('window:resize')
  public onImageLoad(): void {
    this.setImageVisibility(false);
    this.imageElm = this.viewImageElement.nativeElement;
    this.container = this.imageElm.parentElement;

    if (!this.phxParentControlsImageVisibility) {
      requestAnimationFrame(() => {
        this.resetAndRepositionImage();
        this.setImageVisibility(true);
      });
    }

    this.phxOnImageLoadComplete.emit();
  }

  public resetAndRepositionImage(): void {
    this.resetZoom();
    this.resetImageTranform();
    this.fitImageToContainer();
    this.alignImageToContainer();
    this.recalculateImageAndContainerRects();
  }

  public setImageVisibility(isVisible: boolean): void {
    this.isImageVisible = isVisible;
    this.cdr.detectChanges();
  }

  public get percentageZoomText(): string {
    // Prevents displaying NaN% on initial load
    return Number.isFinite(this.imageZoomPercentage) ? `${this.imageZoomPercentage}%` : '';
  }

  public zoomIn(zoomAmount: number = this.ZOOM_VALUE): void {
    if (this.imageZoomPercentage > this.maxZoomValue) return;

    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
    this.setZoomTransition();
    const matrix = this.matrixValues;
    let zoomX = matrix[0];
    let zoomY = matrix[3];

    this.currentScale = zoomY = zoomX += zoomAmount;

    if (this.imageZoomPercentage > this.maxZoomValue) this.currentScale = zoomY = zoomX = this.scaleAtMaxZoom;

    this.setMatrixValues([zoomX, matrix[1], matrix[2], zoomY, matrix[4], matrix[5]]);

    this.animationTimeout = setTimeout(() => {
      this.resetTransition();
      this.recalculateImageAndContainerRects();
    }, this.zoomAnimationTime);
  }

  public zoomOut(zoomAmount: number = this.ZOOM_VALUE): void {
    if (this.currentScale <= this.minScale) return;

    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
    this.setZoomTransition();
    let matrix = this.matrixValues;
    let zoomX = matrix[0];
    let zoomY = matrix[3];

    this.currentScale = zoomY = zoomX -= zoomAmount;

    if (zoomX < this.minScale) this.currentScale = zoomX = zoomY = this.minScale;

    matrix = this.zoomoutWidthReposition(matrix, this.currentScale);
    matrix = this.zoomoutHeightReposition(matrix, this.currentScale);

    this.setMatrixValues([zoomX, matrix[1], matrix[2], zoomY, matrix[4], matrix[5]]);

    this.animationTimeout = setTimeout(() => {
      this.resetTransition();
      this.recalculateImageAndContainerRects();
    }, this.zoomAnimationTime);
  }

  public rotateInDegrees(degrees: number, mimeType?: string): string {
    const canvas = this.convertImageToCanvas(this.imageElm);
    return this.getRotatedImageData(degrees, canvas, this.imageElm, mimeType);
  }

  public resetZoom(): void {
    const matrix = this.matrixValues;
    this.currentScale = matrix[3] = matrix[0] = this.minScale;
    this.setMatrixValues(matrix);
  }

  public startPan(): void {
    if (!this.canPan()) {
      this.panEnabled = false;
      this.positionsArray = [];
      return;
    }

    this.panEnabled = true;
    this.setPanTransition();
    this.positionsArray = [];
  }

  @HostListener('document:mouseup')
  public stopPan(): void {
    this.panEnabled = false;
    this.resetTransition();
    this.positionsArray = [];
  }

  @HostListener('document:mousemove', ['$event'])
  public panImage(event: MouseEvent): void {
    if (!this.panEnabled) {
      this.positionsArray = [];
      return;
    }

    const currentPosition: number[] = [event.pageX, event.pageY];
    this.positionsArray.push(currentPosition);
    const positionsArray = this.positionsArray;
    const arrLength = this.positionsArray.length;
    if (positionsArray.length === 2) {
      this.moveImage(positionsArray);
      this.positionsArray = [];
    }
  }

  private zoomoutWidthReposition(matrix: number[], newScale: number): number[] {
    const imageContainerDimensions = this.imageContainerDimensions;
    const imageWidth = this.imageElm.clientWidth * newScale;
    const containerWidth = imageContainerDimensions.width;
    const originalImageWidth = this.imageElm.clientWidth;
    const originDiffW = (imageWidth - originalImageWidth) / 2;
    const originX = -(this.imageElm.offsetLeft) + originDiffW;
    let xPosition = matrix[4];
    const spaceLeftEmpty = xPosition > originX;
    const spaceRightEmpty = (xPosition + imageWidth) < containerWidth + originX;

    if (imageWidth >= containerWidth) {
      if (spaceLeftEmpty && !spaceRightEmpty) xPosition = originX;
      else if (!spaceLeftEmpty && spaceRightEmpty) xPosition = containerWidth - imageWidth + originX;
    } else if (imageWidth < containerWidth) {
      xPosition = (containerWidth - imageWidth) / 2;
      xPosition += originX;
    }

    matrix[4] = xPosition;

    return matrix;
  }

  private zoomoutHeightReposition(matrix: number[], newScale: number): number[] {
    const imageContainerDimensions = this.imageContainerDimensions;
    const imageHeight = this.imageElm.clientHeight * newScale;
    const contHeight = imageContainerDimensions.height;
    const originalImageHeight = this.imageElm.clientHeight;
    const originDiffH = (imageHeight - originalImageHeight) / 2;
    const originY = -(this.imageElm.offsetTop) + originDiffH;
    let yPosition = matrix[5];
    const spaceTopEmpty = yPosition > originY;
    const spaceBotEmpty = (yPosition + imageHeight) < contHeight + originY;

    if (imageHeight >= contHeight) {
      if (spaceTopEmpty && !spaceBotEmpty) yPosition = originY;
      else if (!spaceTopEmpty && spaceBotEmpty) yPosition = contHeight - imageHeight + originY;
    } else if (imageHeight < contHeight) {
      yPosition = (contHeight - imageHeight) / 2;
      yPosition += originY;
    }

    matrix[5] = yPosition;

    return matrix;
  }

  private moveImage(positionsArray: number[][]) {
    const horizontalDiff = positionsArray[1][0] - positionsArray[0][0];
    const verticalDiff = positionsArray[1][1] - positionsArray[0][1];
    const originalX = this.matrixValues[4];
    const originalY = this.matrixValues[5];
    let matrix = this.matrixValues;

    if (this.shouldImageMoveHorizontally(matrix)) matrix[4] += horizontalDiff;
    if (this.shouldImageMoveVertically(matrix)) matrix[5] += verticalDiff;

    matrix = this.containImageWithinContainerWidth(matrix, originalX);
    matrix = this.containImageWithinContainerHeight(matrix, originalY);

    this.setMatrixValues(matrix);
  }

  private getImageAndContainerSpatialDimensions(xPosition: number, yPosition: number): PhxImageSpatialDimensions {
    const imageDimensions: ClientRect = this.imageDimensions || this.imageElm.getBoundingClientRect();
    const imageContainerDimensions: ClientRect = this.imageContainerDimensions || this.container.getBoundingClientRect();
    const imageWidth = imageDimensions.width;
    const imageHeight = imageDimensions.height;
    const containerWidth = imageContainerDimensions.width;
    const containerHeight = imageContainerDimensions.height;
    const originalImageWidth = this.imageElm.clientWidth;
    const originalImageHeight = this.imageElm.clientHeight;
    const originDiffW = (imageWidth - originalImageWidth) / 2;
    const originDiffH = (imageHeight - originalImageHeight) / 2;
    const originX = -(this.imageElm.offsetLeft) + originDiffW;
    const originY = -(this.imageElm.offsetTop) + originDiffH;
    const spaceLeftEmpty = xPosition > originX;
    const spaceRightEmpty = (xPosition + imageWidth) < containerWidth + originX;
    const spaceTopEmpty = yPosition > originY;
    const spaceBotEmpty = (yPosition + imageHeight) < containerHeight + originY;

    return {
      imageDimensions,
      imageContainerDimensions,
      imageWidth,
      imageHeight,
      containerWidth,
      containerHeight,
      originalImageWidth,
      originalImageHeight,
      originDiffW,
      originDiffH,
      originX,
      originY,
      spaceLeftEmpty,
      spaceRightEmpty,
      spaceTopEmpty,
      spaceBotEmpty,
    };
  }

  private containImageWithinContainerWidth(currentMatrix: number[], originalX: number): number[] {
    const matrix = currentMatrix;
    let xPosition = matrix[4];
    const {
      imageWidth,
      containerWidth,
      originX,
      spaceLeftEmpty,
      spaceRightEmpty,
    } = this.getImageAndContainerSpatialDimensions(xPosition, matrix[5]);

    if (imageWidth >= containerWidth) {
      if (spaceLeftEmpty && !spaceRightEmpty) xPosition = originX;
      else if (!spaceLeftEmpty && spaceRightEmpty) xPosition = containerWidth - imageWidth + originX;
    } else if (imageWidth < containerWidth) {
      if (!spaceLeftEmpty && spaceRightEmpty) xPosition = originX;
      else if (spaceLeftEmpty && !spaceRightEmpty) xPosition = containerWidth - imageWidth + originX;
      else if (spaceLeftEmpty && spaceRightEmpty) xPosition = originalX;
    }

    matrix[4] = xPosition;

    return matrix;
  }

  private containImageWithinContainerHeight(currentMatrix: number[], originalY: number): number[] {
    const matrix = currentMatrix;
    let yPosition = matrix[5];
    const {
      imageHeight,
      containerHeight,
      originY,
      spaceTopEmpty,
      spaceBotEmpty,
    } = this.getImageAndContainerSpatialDimensions(matrix[4], yPosition);

    if (imageHeight >= containerHeight) {
      if (spaceTopEmpty && !spaceBotEmpty) yPosition = originY;
      else if (!spaceTopEmpty && spaceBotEmpty) yPosition = containerHeight - imageHeight + originY;
    } else if (imageHeight < containerHeight) {
      if (!spaceTopEmpty && spaceBotEmpty) yPosition = originY;
      else if (spaceTopEmpty && !spaceBotEmpty) yPosition = containerHeight - imageHeight + originY;
      else if (spaceTopEmpty && spaceBotEmpty) yPosition = originalY;
    }

    matrix[5] = yPosition;

    return matrix;
  }

  private shouldImageMoveHorizontally(matrix: number[]): boolean {
    const {
      spaceLeftEmpty,
      spaceRightEmpty,
    } = this.getImageAndContainerSpatialDimensions(matrix[4], matrix[5]);

    return !spaceLeftEmpty && !spaceRightEmpty;
  }

  private shouldImageMoveVertically(matrix: number[]): boolean {
    const {
      spaceTopEmpty,
      spaceBotEmpty,
    } = this.getImageAndContainerSpatialDimensions(matrix[4], matrix[5]);

    return !spaceTopEmpty && !spaceBotEmpty;
  }

  private canPan(): boolean {
    this.recalculateImageAndContainerRects();
    const {
      imageWidth,
      imageHeight,
      containerWidth,
      containerHeight,
    } = this.getImageAndContainerSpatialDimensions(this.matrixValues[4], this.matrixValues[5]);

    const canPanHorizontally = imageWidth > containerWidth + this.PAN_EPSILON;
    const canPanVertically = imageHeight > containerHeight + this.PAN_EPSILON;

    return canPanHorizontally || canPanVertically;
  }

  private setPanTransition(): void {
    if (!this.imageElm) return;
    this.imageElm.style.transition = 'none 0s ease 0s';
  }

  private resetTransition(): void {
    if (!this.imageElm) return;
    this.imageElm.style.transition = '';
  }

  private setZoomTransition(): void {
    if (!this.imageElm) return;
    this.imageElm.style.transition = `transform ${this.zoomAnimationTime}ms ease-in-out 0s`;
  }

  private resetImageTranform(): void {
    if (!this.imageElm) return;
    this.matrixValues = [1, 0, 0, 1, 0, 0];
    this.setMatrixValues(this.matrixValues);
  }

  private alignImageToContainer(): void {
    this.recalculateImageAndContainerRects();
    const matrix = this.matrixValues;

    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;
    const imageWidth = this.imageElm.clientWidth;
    const imageHeight = this.imageElm.clientHeight;

    const horizontalOffset = (containerWidth - imageWidth) / 2;
    const verticalOffset = (containerHeight - imageHeight) / 2;

    matrix[4] = horizontalOffset;
    matrix[5] = verticalOffset;

    this.setMatrixValues(matrix);
  }

  private fitImageToContainer(): void {
    const imageElm = this.imageElm;
    if (this.phxFitToContainer) this.fitImageToContainerWidthHeight();
    else if (imageElm.naturalHeight > imageElm.naturalWidth) this.fitImageHeightToContainer();
    else if (imageElm.naturalHeight < imageElm.naturalWidth) this.fitImageWidthToContainer();
    else this.fitImageToContainerWidthHeight();

    if (this.phxInitialScale !== 1.0) {
      this.applyInitialScale();
    }
  }

  private fitImageHeightToContainer(): void {
    const imageElm = this.imageElm;
    const container = this.container;
    const scaleToOffset: number = container.clientHeight / imageElm.naturalHeight;
    this.setImageWidthHeightToScale(scaleToOffset);
  }

  private fitImageWidthToContainer(): void {
    const imageElm = this.imageElm;
    const container = this.container;
    const scaleToOffset: number = container.clientWidth / imageElm.naturalWidth;
    this.setImageWidthHeightToScale(scaleToOffset);
  }

  private fitImageToContainerWidthHeight(): void {
    const imageElm = this.imageElm;
    const container = this.container;

    // Calculate scaling factors based on both width and height
    const widthScale = container.clientWidth / imageElm.naturalWidth;
    const heightScale = container.clientHeight / imageElm.naturalHeight;

    // Choose the minimum scaling factor to fit the image within the container while preserving aspect ratio
    const scale = Math.min(widthScale, heightScale);
    this.setImageWidthHeightToScale(scale);
  }

  private setImageWidthHeightToScale(scale: number): void {
    const imageElm = this.imageElm;
    imageElm.height = imageElm.naturalHeight * scale;
    imageElm.width = imageElm.naturalWidth * scale;
  }

  private applyInitialScale(): void {
    const imageElm = this.imageElm;
    const currentWidth = imageElm.width;
    const currentHeight = imageElm.height;

    // Scale down from the current fitted size
    imageElm.width = currentWidth * this.phxInitialScale;
    imageElm.height = currentHeight * this.phxInitialScale;
  }

  private convertImageToCanvas(image: HTMLImageElement): HTMLCanvasElement {
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    canvas.getContext('2d').drawImage(image, 0, 0);
    return canvas;
  }

  private getRotatedImageData(degrees: number, canvas: HTMLCanvasElement, img: HTMLImageElement, mimeType: string = 'image/png'): string {
    // Normalize image type to valid MIME type format converting the type jpg, jpeg to image/jpeg
    let normalizedMimeType = mimeType.toLowerCase();
    if (normalizedMimeType === 'jpg' || normalizedMimeType === 'jpeg') {
      normalizedMimeType = 'image/jpeg';
    } else if (!normalizedMimeType.startsWith('image/')) {
      // default to image/png
      normalizedMimeType = 'image/png';
    }

    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;
    const size = this.newCanvasSize(imgWidth, imgHeight, degrees);
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);

    // save the unrotated context of the canvas so we can restore it later
    // the alternative is to untranslate & unrotate after drawing
    context.save();

    // move to the center of the canvas
    context.translate(canvas.width / 2, canvas.height / 2);

    // rotate the canvas to the specified degrees
    context.rotate(degrees * Math.PI / 180);

    // draw the image
    // since the context is rotated, the image will be rotated also
    context.drawImage(img, -imgWidth / 2, -imgHeight / 2);
    // we're done with the rotating so restore the unrotated context
    context.restore();
    return canvas.toDataURL(normalizedMimeType);
  }

  private newCanvasSize(w: number, h: number, a: number): PhxImagePanZoomSizeObject {
    const rads = a * Math.PI / 180;
    let c = Math.cos(rads);
    let s = Math.sin(rads);
    if (s < 0) s = -s;
    if (c < 0) c = -c;

    const size: PhxImagePanZoomSizeObject = { width: 0, height: 0 };
    size.width = h * s + w * c;
    size.height = h * c + w * s;

    return size;
  }

  private setMatrixValues(matrix: number[]): void {
    if (!this.imageElm) return;
    this.matrixValues = matrix;
    const imageElm = this.imageElm;
    const transformString = `matrix(${matrix[0]}, ${matrix[1]}, ${matrix[2]}, ${matrix[3]}, ${matrix[4]}, ${matrix[5]})`;

    imageElm.style.transform = transformString;
  }

  private recalculateImageAndContainerRects(): void {
    this.imageDimensions = this.imageElm.getBoundingClientRect();
    this.imageContainerDimensions = this.container.getBoundingClientRect();
  }

  private get imageZoomPercentage(): number {
    const imageElm = this.imageElm;
    if (!imageElm) return NaN;
    return Math.floor((imageElm.clientWidth * this.currentScale) / imageElm.naturalWidth * 100);
  }

  private get scaleAtMaxZoom(): number {
    const imageElm = this.imageElm;
    if (!imageElm) return NaN;
    return this.maxZoomValue * imageElm.naturalWidth / imageElm.clientWidth / 100;
  }

  private updateSafeImageUrl(): void {
    if (this.phxImageSrcUrl) {
      this.safeImageUrl = this._sanitizer.bypassSecurityTrustUrl(this.phxImageSrcUrl);
    }
  }
}
